import React, { useState } from 'react';
import { X, CreditCard, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { payCreditsWithAccountBalance } from '../../api/wholesale/distributors';

const AccountBalanceModal = ({
  isOpen,
  onClose,
  distributorId,
  distributorName,
  accountBalance = 0,
  outstandingBalance = 0,
  onSuccess
}) => {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const maxPayableAmount = Math.min(accountBalance, outstandingBalance);

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Allow empty or valid number up to account balance
    if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= accountBalance)) {
      setAmount(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payAmount = parseFloat(amount);

    if (!amount || payAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (payAmount > accountBalance) {
      toast.error('Amount cannot exceed account balance');
      return;
    }

    if (payAmount > outstandingBalance) {
      toast.error('Amount cannot exceed outstanding balance');
      return;
    }

    setLoading(true);
    try {
      await payCreditsWithAccountBalance(
        distributorId,
        payAmount,
        description || 'Payment from account balance'
      );
      toast.success('Payment successful');
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error(error.message || 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAmount('');
    setDescription('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Pay Outstanding Balance
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Body */}
          <div className="px-6 py-4 space-y-4">
            {/* Distributor Info */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-600">Customer</p>
              <p className="font-medium text-gray-900">{distributorName}</p>
            </div>

            {/* Balance Info - Only Outstanding */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                <p className="text-xs text-amber-600">Outstanding Balance</p>
                <p className="text-lg font-bold text-amber-700">
                  ₦{outstandingBalance.toLocaleString()}
                </p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                <p className="text-xs text-emerald-600">Available to Pay</p>
                <p className="text-lg font-bold text-emerald-700">
                  ₦{accountBalance.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Amount Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount (₦)
              </label>
              <input
                type="number"
                value={amount}
                onChange={handleAmountChange}
                placeholder="0.00"
                step="0.01"
                min="0.01"
                max={accountBalance}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              {maxPayableAmount > 0 && (
                <div className="mt-1 flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    Max: ₦{maxPayableAmount.toLocaleString()}
                  </p>
                  <button
                    type="button"
                    onClick={() => setAmount(maxPayableAmount.toString())}
                    className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                  >
                    Use Max
                  </button>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (Optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add a note about this payment..."
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Info Message */}
            <div className="flex gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium">Pay from Account Balance</p>
                <p className="text-xs mt-1">
                  This will deduct from the customer's account balance and reduce their outstanding credit balance.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 px-6 py-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || accountBalance === 0}
              className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 font-medium"
            >
              {loading ? 'Processing...' : 'Pay Now'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccountBalanceModal;
